import { useEffect, useState } from 'react';

const appBaseUrl = import.meta.env.VITE_FLUTTER_APP_URL?.replace(/\/$/, '');

const navLinks = [
  { label: 'How it works', href: '#how-it-works' },
  { label: 'Features', href: '#features' },
  { label: 'For buyers', href: '#buyers' },
  { label: 'For sellers', href: '#sellers' },
  { label: 'Resources', href: '#resources' },
  { label: 'FAQ', href: '#faq' }
];

const valueBullets = [
  {
    title: 'Verified local agents',
    copy: 'Only community-reviewed agents handle your job. You can see inspection history, specialties, and turnaround times before you commit.'
  },
  {
    title: 'South Africa-first coverage',
    copy: 'Built for Johannesburg, Cape Town, Durban and beyond with local pricing knowledge, licensing know-how, and routes for test drives and inspections.'
  },
  {
    title: 'Protect your spend',
    copy: 'Protected payment flows keep funds secure until the work is signed off, so buyers and sellers move quickly without worrying about scams.'
  },
  {
    title: 'Transparent documentation',
    copy: 'Agents help with roadworthy checks, logbook validation, and transfer paperwork so you are never surprised by admin at the end.'
  },
  {
    title: 'Live status and chat',
    copy: 'Track every step, share photos, and speak directly to the agent that is inspecting, pricing, or selling the vehicle for you.'
  },
  {
    title: 'Confidence before you pay',
    copy: 'Pre-purchase car inspections, used car valuations, and sell-my-car guidance live in one thread with photos, so you can decide fast.'
  }
];

const features = [
  {
    title: 'Browse agent services',
    description:
      'Agents publish what they offer: pre-purchase inspections, viewing support, listing prep, negotiation help, or sourcing. You can compare scope, pricing style, and recent reviews before booking.'
  },
  {
    title: 'Book inspections with evidence',
    description:
      'An agent views the vehicle, checks the basics, and shares photos and notes in one thread so you can decide to move forward or walk away.'
  },
  {
    title: 'Listing and selling support',
    description:
      'If you are selling, agents help with photos, wording, used car valuation guidance, and managing buyer interest so you can sell your car fast without pressure.'
  },
  {
    title: 'On-site viewing help',
    description:
      'Need someone at a test drive or meet-up? Book an agent to attend, keep records, and advise on next steps while keeping both sides accountable.'
  },
  {
    title: 'Reputation you can see',
    description:
      'Every completed job earns a review. Over time the best agents surface through ratings and reliable delivery, making it easier to pick with confidence.'
  }
];

const howItWorks = [
  {
    title: 'Browse services and agents',
    detail:
      'Search the marketplace, read reviews, and compare what agents offer—inspections, viewing support, listing help, or sourcing.'
  },
  {
    title: 'Open a chat and agree the scope',
    detail: 'Message the agent you want. Confirm what is included, the price, and when you need it done.'
  },
  {
    title: 'Track updates in one thread',
    detail: 'Get photos, notes, and quick status updates as the work happens so you always know what is next.'
  },
  {
    title: 'Approve and review',
    detail: 'When the work matches what was agreed, pay and leave a review. Great delivery builds agent reputation.'
  }
];

const useCases = [
  {
    id: 'buyers',
    title: 'For car buyers',
    description:
      'Avoid buying blind. A nearby agent handles a pre-purchase car inspection, shares photos and notes, and gives you a clear proceed-or-pause view.',
    detail:
      'If you are comparing listings, E-Moto helps you check vehicle history, verify paperwork, and price the car using real market signals.',
    bullets: [
      'Book a car inspection near you in South Africa without dealership pressure.',
      'Get a vehicle history check and written inspection report in one chat.',
      'Use a used car price guide and valuation notes to negotiate.'
    ]
  },
  {
    id: 'sellers',
    title: 'For sellers',
    description:
      'Price and present your car correctly the first time. Agents help with photos, wording, pricing expectations, screening buyers, and keeping a record of offers.',
    detail:
      'When you want to sell your car fast or privately, you get help with listings, roadworthy requirements, and safer handovers.',
    bullets: [
      'Set a realistic asking price with used car valuation support.',
      'Prepare roadworthy certificate and transfer paperwork early.',
      'Share inspection evidence to build trust with serious buyers.'
    ]
  },
  {
    id: 'agents',
    title: 'For agents and dealers',
    description:
      'Join a platform that rewards transparent service. Build a reviewed profile, manage bookings, and close work with protected payments.',
    detail:
      'Deliver inspection reports, valuations, and listing support inside one thread so customers can approve quickly and leave reviews.',
    bullets: [
      'Publish services with clear scope and pricing.',
      'Keep evidence, photos, and timestamps in one job record.',
      'Grow reputation with public reviews and repeat customers.'
    ]
  }
];

const trustSignals = [
  {
    quote:
      'Buyer in Gauteng: Pre-purchase inspection booked, report delivered with photos, and negotiation points flagged before committing to pay.',
    name: 'Example story',
    role: 'Buying a family SUV'
  },
  {
    quote:
      'Seller in Cape Town: Agent prepared photos, screened calls, and used protected payments for a safe handover so the transaction stayed on track.',
    name: 'Example story',
    role: 'Listing a private sale'
  },
  {
    quote:
      'Dealership in Durban: Uses E-Moto inspectors to keep defects, photos, and test results in a single thread for faster approvals.',
    name: 'Example story',
    role: 'Supporting weekly listings'
  }
];

const faqs = [
  {
    question: 'Is E-Moto available across South Africa?',
    answer:
      'Yes. We operate across major metros including Johannesburg, Pretoria, Cape Town, Durban, and surrounding towns. Tell us where you are and we will match you with nearby agents who know the local roads, licensing offices, and inspection partners.'
  },
  {
    question: 'What is included in a pre-purchase car inspection?',
    answer:
      'A local agent checks the vehicle in person, verifies VIN and engine numbers, documents condition with photos, and flags red flags or negotiation points. You receive a clear report inside E-Moto so you can proceed or pause with confidence.'
  },
  {
    question: 'How much does a pre-purchase inspection cost in South Africa?',
    answer:
      'Costs depend on the vehicle type and location. Agents publish their pricing upfront, and you see the quote before you approve the booking so there are no surprises.'
  },
  {
    question: 'How do I check a vehicle history in South Africa?',
    answer:
      'Agents can run vehicle history checks and compare records with the physical inspection. You get evidence in one place, helping you avoid accident history or paperwork issues.'
  },
  {
    question: 'Can E-Moto help me sell my car fast or privately?',
    answer:
      'Yes. Agents help with listings, photos, pricing, and buyer screening so you can sell your car fast without getting flooded by time-wasters.'
  },
  {
    question: 'Do you provide used car valuation or price guidance?',
    answer:
      'Agents compare live listings and recent sales to estimate a fair range, giving you a practical used car price guide for negotiation or selling.'
  },
  {
    question: 'Do you help with roadworthy certificates and paperwork?',
    answer:
      'Yes. Agents guide you through roadworthy certificate requirements, transfer paperwork, and the documents needed to buy or sell a car in South Africa.'
  },
  {
    question: 'How do payments stay secure?',
    answer:
      'Payments are held in a protected flow until work is signed off. You approve milestones such as an inspection report or listing draft before funds are released.'
  },
  {
    question: 'Do I need to download an app?',
    answer:
      'You can open the E-Moto experience in your browser immediately. If you prefer mobile, add the app to your home screen for faster access; all chats and orders stay in sync.'
  },
  {
    question: 'How fast can I start?',
    answer:
      'Most people start a chat within minutes and receive inspection availability within the same day. For sellers, listings are typically prepared within 24 to 48 hours once photos are captured.'
  }
];

const buttonBaseClasses =
  'rounded-full px-8 py-3 text-sm font-semibold uppercase tracking-[0.22em] shadow-md transition-transform duration-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-brand-light hover:-translate-y-1 hover:shadow-2xl disabled:cursor-not-allowed disabled:opacity-60';

const buildAppUrl = (path: string) => {
  if (!appBaseUrl) {
    return path;
  }
  const normalizedBase = appBaseUrl.endsWith('/') ? appBaseUrl.slice(0, -1) : appBaseUrl;
  return `${normalizedBase}${path}`;
};

export const LandingPage = () => {
  const [heroReady, setHeroReady] = useState(false);
  const isConfigured = Boolean(appBaseUrl);

  useEffect(() => {
    setHeroReady(true);
  }, []);

  const renderFeatureIcon = (index: number) => {
    const baseClass = 'h-10 w-10 rounded-2xl bg-brand/10 text-brand-dark p-2';
    switch (index) {
      case 0:
        return (
          <div className={baseClass} aria-hidden="true">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" className="h-full w-full">
              <circle cx="12" cy="12" r="9" />
              <path d="M8.5 13.5 11 15l4.5-6" />
            </svg>
          </div>
        );
      case 1:
        return (
          <div className={baseClass} aria-hidden="true">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" className="h-full w-full">
              <path d="M4 9h16l-1.2 8H5.2L4 9Z" />
              <path d="M7 9V6a5 5 0 0 1 10 0v3" />
            </svg>
          </div>
        );
      case 2:
        return (
          <div className={baseClass} aria-hidden="true">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" className="h-full w-full">
              <rect x="4" y="5" width="16" height="12" rx="2" />
              <path d="M4 10h16" />
              <path d="M9 15h6" />
            </svg>
          </div>
        );
      case 3:
        return (
          <div className={baseClass} aria-hidden="true">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" className="h-full w-full">
              <path d="M6 4h12l2 4H4l2-4Z" />
              <path d="M4 8v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8" />
              <path d="M9 13h6" />
            </svg>
          </div>
        );
      default:
        return (
          <div className={baseClass} aria-hidden="true">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" className="h-full w-full">
              <path d="M4 7h16v10H4Z" />
              <path d="M4 11h16" />
              <path d="M9 15h6" />
            </svg>
          </div>
        );
    }
  };

  return (
    <div
      id="top"
      className="relative min-h-screen overflow-hidden bg-[radial-gradient(circle_at_12%_12%,rgba(24,170,153,0.2),transparent_45%),radial-gradient(circle_at_88%_8%,rgba(243,195,68,0.18),transparent_40%),linear-gradient(to_bottom,#f7fffd,#ffffff_55%,#f6f8ff)] text-brand-text"
    >
      <div className="pointer-events-none absolute inset-x-0 top-0 -z-10 flex justify-center">
        <div className="h-[28rem] w-[44rem] translate-y-[-35%] rounded-full bg-gradient-to-br from-brand/30 via-brand-light/45 to-transparent blur-[180px]" />
      </div>

      <header className="mx-auto mt-6 flex w-full max-w-6xl items-center justify-between gap-4 rounded-3xl border border-white/80 bg-white/90 px-6 py-4 shadow-xl shadow-brand/15 backdrop-blur">
        <div className="flex items-center gap-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-gradient-to-br from-brand/80 to-brand-dark text-white shadow">
            <span className="text-lg font-semibold">E</span>
          </div>
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-[0.32em] text-brand-subtext">E-Moto</p>
            <p className="text-base font-semibold text-brand-text">Verified agents for buying and selling cars</p>
          </div>
        </div>
        <div className="flex items-center gap-4">
          <nav aria-label="Primary" className="hidden items-center gap-6 text-sm font-semibold text-brand-subtext md:flex">
            {navLinks.map((link) => (
              <a key={link.href} href={link.href} className="transition hover:text-brand-dark">
                {link.label}
              </a>
            ))}
          </nav>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-6 pt-12 pb-16">
        <section className="mb-8 flex flex-col gap-4 rounded-3xl border border-white/80 bg-gradient-to-r from-white via-white/90 to-brand-alt/70 p-5 shadow-xl shadow-brand/10 md:flex-row md:items-center md:justify-between">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.3em] text-brand-dark">Start here</p>
            <p className="mt-2 text-base font-semibold text-brand-text">
              Open E-Moto to compare agents, request an inspection, or list your car.
            </p>
          </div>
          <a
            href={isConfigured ? buildAppUrl('/NavigationPage') : '#'}
            target="_blank"
            rel="noreferrer"
            aria-disabled={!isConfigured}
            className={`rounded-full bg-brand-dark px-10 py-4 text-sm font-semibold uppercase tracking-[0.28em] text-white shadow-2xl shadow-brand/40 transition hover:-translate-y-0.5 hover:bg-brand-dark/95 ${
              isConfigured ? '' : 'cursor-not-allowed opacity-60'
            }`}
            onClick={(event) => {
              if (!isConfigured) {
                event.preventDefault();
              }
            }}
          >
            Open E-Moto
          </a>
        </section>
        <section
          id="hero"
          className="relative grid gap-12 overflow-hidden rounded-[36px] border border-white/80 bg-white/85 p-8 shadow-2xl shadow-brand/15 ring-1 ring-white/70 lg:grid-cols-[1.1fr,0.9fr] lg:items-center"
        >
          <div className="pointer-events-none absolute inset-0 -z-10 bg-gradient-to-br from-brand-light/35 via-white/40 to-brand-alt/70" />
          <div className="pointer-events-none absolute -right-24 -top-24 h-52 w-52 rounded-full bg-gradient-to-br from-brand/30 via-brand-light/30 to-transparent blur-3xl" />
          <div className="pointer-events-none absolute -left-28 -bottom-28 h-56 w-56 rounded-full bg-gradient-to-tr from-brand/25 via-white/40 to-transparent blur-3xl" />
          <div className="relative space-y-5">
            <span
              className={`inline-flex items-center gap-2 rounded-full bg-white/80 px-4 py-1 text-[11px] font-semibold uppercase tracking-[0.32em] text-brand-dark shadow transition duration-700 ${
                heroReady ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-3'
              }`}
            >
              Pre-purchase inspections • Used car pricing • South Africa
            </span>
            <h1
              className={`font-display text-4xl font-semibold leading-tight text-brand-text md:text-5xl md:leading-[1.08] tracking-tight transition duration-700 ${
                heroReady ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
              }`}
            >
              E-Moto is the trusted marketplace for automotive services in South Africa, connecting you with vetted agents for inspections,
              valuations, and selling support.
            </h1>
            <p
              className={`text-lg text-brand-subtext transition duration-700 delay-75 ${
                heroReady ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-3'
              }`}
            >
              Use E-Moto to book a pre-purchase car inspection, request a used car valuation, or get help selling your car fast and safely.
              Local agents in Johannesburg, Cape Town, Durban and beyond handle viewings, photos, paperwork, and vehicle history checks,
              then share a report in one chat. You compare ratings, agree on scope and pricing, and approve milestones before payment is
              released, so you can decide with confidence.
            </p>
            <div
              className={`flex flex-wrap gap-4 transition duration-700 delay-150 ${
                heroReady ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-3'
              }`}
            >
              <a
                href="#features"
                className={`${buttonBaseClasses} bg-brand-dark text-white shadow-lg shadow-brand/30 hover:bg-brand-dark/95`}
              >
                See what agents do
              </a>
              <a
                href="#how-it-works"
                className={`${buttonBaseClasses} border border-brand-dark text-brand-dark hover:bg-brand-dark hover:text-white`}
              >
                How it works
              </a>
            </div>
            <p
              className={`text-sm font-medium text-brand-dark transition duration-700 delay-200 ${
                heroReady ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2'
              }`}
            >
              Community-reviewed agents • Protected payments • Car inspection near you in South Africa
            </p>
            {!isConfigured && (
              <p className="mt-2 text-sm font-medium text-red-600">
                Set <code className="rounded bg-red-50 px-1">VITE_FLUTTER_APP_URL</code> to enable deep links into the E-Moto app.
              </p>
            )}
          </div>
          <div className="space-y-6">
            <article className="relative overflow-hidden rounded-3xl border border-[#DFEDEC] bg-white/95 p-6 shadow-xl shadow-brand/15">
              <div className="pointer-events-none absolute right-[-3rem] top-[-3rem] h-32 w-32 rounded-full bg-gradient-to-br from-brand-light/70 to-brand/10 blur-3xl" />
              <p className="text-xs font-semibold uppercase tracking-[0.38em] text-brand-dark">Sample view</p>
              <h2 className="mt-3 font-display text-2xl font-semibold text-brand-text">Inspection notes in one place</h2>
              <p className="mt-3 text-base text-brand-subtext">
                Agents share photos and written notes inside one thread so you can review the work without switching tools.
              </p>
              <ul className="mt-4 space-y-2 text-sm text-brand-subtext">
                {['Photos from the inspection', 'Written observations', 'Follow-up questions in chat'].map((item) => (
                  <li key={item} className="flex items-center gap-2">
                    <span className="inline-flex h-2 w-2 rounded-full bg-brand-dark" aria-hidden="true" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-4 flex flex-wrap gap-3">
                {['Photos + notes', 'Agent reviews', 'Order updates'].map((pill) => (
                  <span
                    key={pill}
                    className="inline-flex items-center rounded-full bg-brand/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.22em] text-brand-dark"
                  >
                    {pill}
                  </span>
                ))}
              </div>
            </article>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="rounded-3xl border border-[#e6f2ee] bg-brand-surface/90 p-5 shadow-lg shadow-brand/10 transition hover:-translate-y-1 hover:shadow-xl">
                <p className="text-xs font-semibold uppercase tracking-[0.28em] text-brand-dark">Valuation snapshot</p>
                <h3 className="mt-2 font-display text-xl font-semibold text-brand-text">Used car price guidance</h3>
                <p className="mt-2 text-sm text-brand-subtext">
                  Agents compare live listings and local demand to estimate a fair range before you negotiate.
                </p>
              </div>
              <div className="rounded-3xl border border-[#e6f2ee] bg-brand-surface/90 p-5 shadow-lg shadow-brand/10 transition hover:-translate-y-1 hover:shadow-xl">
                <p className="text-xs font-semibold uppercase tracking-[0.28em] text-brand-dark">Sale readiness</p>
                <h3 className="mt-2 font-display text-xl font-semibold text-brand-text">Sell with confidence</h3>
                <p className="mt-2 text-sm text-brand-subtext">
                  Get listing prep, roadworthy guidance, and safer handovers in one workflow.
                </p>
              </div>
            </div>
            <div className="rounded-3xl border border-[#e6f2ee] bg-white/90 p-5 shadow-sm shadow-brand/10">
              <p className="text-xs font-semibold uppercase tracking-[0.28em] text-brand-dark">App workflow</p>
              <div className="mt-3 grid gap-3 sm:grid-cols-3">
                {['Choose an agent', 'Track updates', 'Approve and pay'].map((step) => (
                  <div
                    key={step}
                    className="rounded-2xl border border-white/70 bg-brand-alt/60 px-3 py-2 text-sm font-semibold text-brand-text shadow-sm shadow-brand/10"
                  >
                    {step}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section
          id="value"
          className="relative mt-14 overflow-hidden rounded-3xl border border-white/80 bg-white/85 p-8 shadow-xl shadow-brand/10"
        >
          <div className="pointer-events-none absolute right-[-3rem] top-[-4rem] h-40 w-40 rounded-full bg-gradient-to-br from-brand-light/50 to-brand/10 blur-3xl" />
          <div className="pointer-events-none absolute left-[-3rem] bottom-[-4rem] h-44 w-44 rounded-full bg-gradient-to-tr from-brand/20 to-brand-light/5 blur-3xl" />
          <p className="text-xs uppercase tracking-[0.4em] text-brand-dark">Why choose E-Moto</p>
          <h2 className="mt-3 font-display text-3xl font-semibold text-brand-text">People-powered automotive marketplace</h2>
          <p className="mt-4 text-brand-subtext">
            E-Moto connects customers with automotive agents who publish services, deliver the work, and earn reviews. It is closer to a
            services marketplace than a classifieds site—agents win trust by completing jobs well, and customers see ratings before they book.
          </p>
          <p className="mt-4 text-brand-subtext">
            Typical services include pre-purchase inspections, viewing support, listing and negotiation help, used car price guidance,
            paperwork support, and car sourcing. Everything runs through one thread so you know what is happening and what you are paying for.
          </p>
          <div className="mt-8 grid gap-6 md:grid-cols-2 xl:grid-cols-3">
            {valueBullets.map((item) => (
              <article
                key={item.title}
                className="flex h-full flex-col gap-3 rounded-3xl border border-[#e6f2ee] bg-white/95 p-6 shadow-lg shadow-brand/10 transition hover:-translate-y-1 hover:shadow-xl"
              >
                <h3 className="font-display text-xl font-semibold text-brand-text">{item.title}</h3>
                <p className="text-sm text-brand-subtext">{item.copy}</p>
              </article>
            ))}
          </div>
        </section>

        <section id="features" className="mt-14 grid gap-8 lg:grid-cols-[1.1fr,0.9fr]">
          <div className="rounded-3xl border border-white/80 bg-white/90 p-8 shadow-xl shadow-brand/10">
            <p className="text-xs uppercase tracking-[0.38em] text-brand-dark">Features</p>
            <h2 className="mt-3 font-display text-3xl font-semibold text-brand-text">What you can do with E-Moto</h2>
            <p className="mt-4 text-brand-subtext">
              E-Moto combines verified agents, transparent pricing, and guided workflows. Each feature is built to answer the
              questions people ask when they search for used cars, inspections, or selling support in South Africa.
            </p>
            <div className="mt-8 space-y-4">
              {features.map((feature, index) => (
                <article
                  key={feature.title}
                  className="flex gap-4 rounded-3xl border border-[#dcefeb] bg-brand-surface/80 p-6 shadow-md shadow-brand/10 transition hover:-translate-y-1 hover:shadow-xl"
                >
                  {renderFeatureIcon(index)}
                  <div>
                    <h3 className="font-display text-xl font-semibold text-brand-text">{feature.title}</h3>
                    <p className="mt-2 text-sm text-brand-subtext">{feature.description}</p>
                  </div>
                </article>
              ))}
            </div>
          </div>
          <aside className="rounded-3xl border border-[#0f2c2a] bg-gradient-to-br from-[#0f2a27] via-[#0a1b19] to-[#050c0b] p-8 text-white shadow-2xl">
            <p className="text-sm uppercase tracking-[0.6em] text-brand-light">How it works in practice</p>
            <h2 className="mt-4 font-display text-2xl font-semibold">One thread, one agent, clear approvals</h2>
            <p className="mt-4 text-white/80">
              You pick an agent based on reviews, agree on scope, and track updates in a single thread with photos and timestamps. When
              the work matches what was promised, you approve and release payment. Simple marketplace mechanics, built for automotive jobs.
            </p>
            <p className="mt-6 text-sm font-semibold text-white/85">Mobile-friendly • Clear scopes • Review-driven trust</p>
          </aside>
        </section>

        <section id="how-it-works" className="mt-14 rounded-3xl border border-white/80 bg-white/85 p-8 shadow-lg shadow-brand/10">
          <div className="flex flex-col gap-4 md:flex-row md:items-baseline md:justify-between">
            <div>
              <p className="text-xs uppercase tracking-[0.4em] text-brand-dark">How it works</p>
              <h2 className="mt-3 font-display text-3xl font-semibold text-brand-text">Four steps to work with an agent</h2>
            </div>
            <p className="max-w-xl text-brand-subtext">You choose the agent, agree the scope, and keep every update in one place.</p>
          </div>
          <div className="mt-8 grid gap-6 md:grid-cols-2 xl:grid-cols-4">
            {howItWorks.map((step, index) => (
              <article
                key={step.title}
                className="flex h-full flex-col gap-3 rounded-3xl border border-[#e6f2ee] bg-white/95 p-6 shadow-lg shadow-brand/10 ring-1 ring-brand/5 transition hover:-translate-y-1 hover:shadow-xl"
              >
                <div className="flex items-center gap-3">
                  <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-brand-dark text-xs font-semibold uppercase tracking-[0.2em] text-white">
                    {index + 1}
                  </span>
                  <p className="text-[11px] font-semibold uppercase tracking-[0.32em] text-brand-dark">Step {index + 1}</p>
                </div>
                <h3 className="font-display text-xl font-semibold text-brand-text">{step.title}</h3>
                <p className="text-sm text-brand-subtext">{step.detail}</p>
              </article>
            ))}
          </div>
        </section>

        <section id="buyers" className="mt-14 rounded-3xl border border-white/80 bg-white/90 p-8 shadow-lg shadow-brand/10">
          <div className="flex flex-col gap-4 md:flex-row md:items-baseline md:justify-between">
            <div>
              <p className="text-xs uppercase tracking-[0.4em] text-brand-dark">Use cases</p>
              <h2 className="mt-3 font-display text-3xl font-semibold text-brand-text">Built for buyers, sellers, agents, and fleets</h2>
            </div>
            <p className="max-w-xl text-brand-subtext">
              Choose the scenario that fits you. Every path keeps content, pricing, and steps visible before you open the app.
            </p>
          </div>
          <div className="mt-8 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {useCases.map((useCase) => (
              <article
                key={useCase.id}
                id={useCase.id}
                className="rounded-3xl border border-[#e6f2ee] bg-brand-surface/80 p-6 shadow-lg shadow-brand/10 transition hover:-translate-y-1 hover:shadow-xl"
              >
                <h3 className="font-display text-xl font-semibold text-brand-text">{useCase.title}</h3>
                <p className="mt-2 text-sm text-brand-subtext">{useCase.description}</p>
                <p className="mt-3 text-sm text-brand-subtext">{useCase.detail}</p>
                <ul className="mt-4 space-y-2 text-sm text-brand-subtext">
                  {useCase.bullets.map((bullet) => (
                    <li key={bullet} className="flex gap-2">
                      <span className="mt-1 h-2 w-2 rounded-full bg-brand-dark/70" aria-hidden="true" />
                      <span>{bullet}</span>
                    </li>
                  ))}
                </ul>
              </article>
            ))}
          </div>
        </section>

        <section id="trust" className="mt-14 rounded-3xl border border-white/80 bg-gradient-to-br from-brand-surface/80 via-white to-brand-alt/60 p-8 shadow-lg shadow-brand/10">
          <div className="flex flex-col gap-3">
            <div className="flex items-center gap-3">
              <p className="text-xs uppercase tracking-[0.4em] text-brand-dark">Trust & social proof</p>
              <span className="rounded-full bg-white px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.24em] text-brand-subtext">
                Example scenarios
              </span>
            </div>
            <h2 className="font-display text-3xl font-semibold text-brand-text">Transparent results from South African drivers</h2>
            <p className="max-w-3xl text-brand-subtext">
              Example scenarios based on common E-Moto jobs in Johannesburg, Cape Town, and Durban. Replace with real customer quotes
              once approvals are available.
            </p>
          </div>
          <div className="mt-8 grid gap-6 md:grid-cols-3">
            {trustSignals.map((item) => (
              <article
                key={item.name}
                className="flex h-full flex-col justify-between rounded-3xl border border-[#e6f2ee] bg-white/95 p-6 shadow-lg shadow-brand/10 ring-1 ring-brand/5 transition hover:-translate-y-1 hover:shadow-xl"
              >
                <p className="text-brand-subtext">“{item.quote}”</p>
                <div className="mt-4">
                  <p className="font-display text-base font-semibold text-brand-text">{item.name}</p>
                  <p className="text-sm text-brand-subtext">{item.role}</p>
                </div>
              </article>
            ))}
          </div>
        </section>

        <section
          id="resources"
          className="mt-14 grid gap-8 rounded-3xl border border-white/80 bg-white/90 p-8 shadow-lg shadow-brand/10"
        >
          <div className="flex flex-col gap-3">
            <p className="text-xs uppercase tracking-[0.4em] text-brand-dark">Resources</p>
            <h2 className="font-display text-3xl font-semibold text-brand-text">Learn before you buy or sell</h2>
            <p className="max-w-3xl text-brand-subtext">
              These guides answer common searches like used car valuation, vehicle history check, and paperwork to sell a car in South Africa.
              Read the resources, then open E-Moto when you are ready to book an agent.
            </p>
          </div>
          <div className="grid gap-6 md:grid-cols-3">
            {[
              {
                title: 'Pre-purchase inspection costs',
                description: 'Understand typical inspection pricing and what reports include.',
                href: '/blog/pre-purchase-inspection-costs'
              },
              {
                title: 'Used car buying checklist',
                description: 'A step-by-step checklist for buying a used car safely in SA.',
                href: '/blog/used-car-buying-checklist'
              },
              {
                title: 'Paperwork to sell your car',
                description: 'Documents and transfer steps to sell a car privately in South Africa.',
                href: '/blog/paperwork-to-sell-car-sa'
              }
            ].map((resource) => (
              <article
                key={resource.href}
                className="flex h-full flex-col justify-between rounded-3xl border border-[#e6f2ee] bg-white p-6 shadow-md shadow-brand/10 transition hover:-translate-y-1 hover:shadow-xl"
              >
                <div>
                  <h3 className="font-display text-xl font-semibold text-brand-text">{resource.title}</h3>
                  <p className="mt-2 text-sm text-brand-subtext">{resource.description}</p>
                </div>
                <a href={resource.href} className="mt-4 text-sm font-semibold text-brand-dark underline underline-offset-4">
                  Read the guide
                </a>
              </article>
            ))}
          </div>
          <div className="flex flex-wrap items-center gap-4">
            <a href="/blog" className="text-sm font-semibold text-brand-dark underline underline-offset-4">
              Explore the full blog
            </a>
            <a href="/faq" className="text-sm font-semibold text-brand-dark underline underline-offset-4">
              Visit the FAQ page
            </a>
          </div>
        </section>

        <section
          id="get-started"
          className="mt-14 grid gap-8 rounded-3xl border border-[#0f2c2a] bg-gradient-to-br from-[#0f2a27] via-[#0b2421] to-[#0b3b33] p-8 text-white shadow-2xl"
        >
          <div className="flex flex-col gap-3">
            <p className="text-[11px] font-semibold uppercase tracking-[0.4em] text-brand-light">Ready to move?</p>
            <h2 className="font-display text-3xl font-semibold">Open the E-Moto experience when you are ready</h2>
            <p className="max-w-3xl text-white/85">
              Start in your browser or jump into the app. The same conversations, inspection notes, and approvals follow you. If you want
              more detail first, keep reading the FAQs or explore services below.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-4">
            <a href="#faq" className={`${buttonBaseClasses} border border-white/70 text-white hover:bg-white hover:text-brand-dark`}>
              View FAQs
            </a>
            <a href="/blog" className="text-sm font-semibold underline decoration-white/40 underline-offset-4 hover:decoration-white">
              Read the blog
            </a>
          </div>
        </section>

        <section id="faq" className="mt-14 rounded-3xl border border-white/80 bg-white/90 p-8 shadow-lg shadow-brand/10">
          <p className="text-xs uppercase tracking-[0.4em] text-brand-dark">Frequently asked questions</p>
          <h2 className="mt-3 font-display text-3xl font-semibold text-brand-text">Get answers before you open the app</h2>
          <p className="mt-3 text-brand-subtext">
            These answers help people searching for "pre-purchase car inspection", "sell my car fast", or "car marketplace South Africa"
            understand how E-Moto works without leaving this page.
          </p>
          <div className="mt-8 grid gap-6 md:grid-cols-2">
            {faqs.map((item) => (
              <article
                key={item.question}
                className="rounded-3xl border border-[#e6f2ee] bg-brand-surface/80 p-6 shadow-md shadow-brand/10 transition hover:-translate-y-1 hover:shadow-xl"
              >
                <h3 className="font-display text-xl font-semibold text-brand-text">{item.question}</h3>
                <p className="mt-2 text-sm text-brand-subtext">{item.answer}</p>
              </article>
            ))}
          </div>
        </section>
      </main>

      <footer className="border-t border-[#DFEDEC] bg-white/90 backdrop-blur">
        <div className="mx-auto flex max-w-6xl flex-col gap-6 px-6 py-10 text-sm text-brand-subtext md:flex-row md:items-start md:justify-between">
          <div>
            <p className="font-display text-base font-semibold text-brand-text">E-Moto</p>
            <p className="mt-1 text-brand-subtext">South Africa</p>
          </div>
          <div className="flex flex-col gap-2">
            <p className="font-display text-base font-semibold text-brand-text">Links</p>
            <a href="/faq" className="transition hover:text-brand-dark">
              FAQ
            </a>
            <a href="/blog" className="transition hover:text-brand-dark">
              Blog
            </a>
            <a href="https://e-moto.co.za/agents" target="_blank" rel="noreferrer" className="transition hover:text-brand-dark">
              Find agents
            </a>
            <a href="https://e-moto.co.za/reviews" target="_blank" rel="noreferrer" className="transition hover:text-brand-dark">
              Community reviews
            </a>
          </div>
          <div className="flex flex-col gap-2">
            <p className="font-display text-base font-semibold text-brand-text">Support</p>
            <a href="mailto:hello@e-moto.africa" className="transition hover:text-brand-dark">
              Contact support
            </a>
            <a href="#top" className="transition hover:text-brand-dark">
              Back to top
            </a>
          </div>
        </div>
        <div className="border-t border-[#DFEDEC] bg-white/70 py-4 text-center text-xs text-brand-subtext">
          © {new Date().getFullYear()} E-Moto. All rights reserved.
        </div>
      </footer>

      <div className="pointer-events-none fixed inset-x-0 bottom-0 z-20 flex justify-center md:hidden">
        <div className="pointer-events-auto mx-4 mb-4 flex w-full max-w-md items-center justify-between rounded-full border border-brand-dark/15 bg-white/95 px-4 py-3 shadow-lg shadow-brand/20">
          <div>
            <p className="text-sm font-semibold text-brand-dark">Explore E-Moto</p>
            <p className="text-xs text-brand-subtext">See services • Learn the workflow</p>
          </div>
          <a
            href="#how-it-works"
            className="rounded-full bg-brand-dark px-4 py-2 text-xs font-semibold uppercase tracking-[0.24em] text-white shadow-md hover:bg-brand focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-light"
          >
            Start
          </a>
        </div>
      </div>
    </div>
  );
};
